import type { ReactNode } from "react";
import { useMemo, useState, useEffect, useCallback } from "react";
import { Link } from "@tanstack/react-router";
import {
  useFetchClubPostsQuery,
  useFetchClubEventsQuery,
  useFetchUserProfilesByIdsQuery,
  useFetchClubMembersQuery,
  type Post,
  type EventOccurrence,
} from "@/api/clubsApi";
import { buildMentionMap } from "@tinyclub/shared/utils";
import { isOwnerOrCohost } from "@tinyclub/shared/models";
import { PostCard } from "@/components/posts/PostCard";
import { EventCard, type EventCardHost } from "@/components/events/EventCard";
import { Skeleton } from "@/components/ui/Skeleton";
import { Icon } from "@/components/ui/Icon";
import { useIntersectionObserver } from "@/hooks/useIntersectionObserver";

interface WhatsHappeningFeedProps {
  clubId: string;
  userId?: string | undefined;
  onPostClick?: (postId: string) => void;
  onCommentClick?: (postId: string) => void;
  onEventClick?: (eventId: string) => void;
  onEditPost?: (post: Post) => void;
  /** Optional post prompt to render at the top inside the container */
  postPrompt?: ReactNode;
  /** Event overflow menu callbacks */
  canEditEvent?: (event: EventOccurrence) => boolean;
  canDeleteEvent?: (event: EventOccurrence) => boolean;
  onEditEvent?: (event: EventOccurrence) => void;
  onDeleteEvent?: (event: EventOccurrence) => void;
}

// Union type for feed items
type FeedItem =
  | { type: "post"; data: Post; createdAt: Date }
  | { type: "event"; data: EventOccurrence; createdAt: Date };

const POSTS_PAGE_SIZE = 20;

export function WhatsHappeningFeed({
  clubId,
  userId,
  onPostClick,
  onCommentClick,
  onEventClick,
  onEditPost,
  postPrompt,
  canEditEvent,
  canDeleteEvent,
  onEditEvent,
  onDeleteEvent,
}: WhatsHappeningFeedProps) {
  const [postsOffset, setPostsOffset] = useState(0);
  const [allPosts, setAllPosts] = useState<Post[]>([]);
  const [hasMorePosts, setHasMorePosts] = useState(true);

  // Fetch posts for the club
  const {
    data: postsData,
    isLoading: postsLoading,
    isFetching: postsFetching,
    error: postsError,
  } = useFetchClubPostsQuery({
    clubId,
    userId: userId || undefined,
    offset: postsOffset,
    limit: POSTS_PAGE_SIZE,
  }, {
    pollingInterval: 15_000,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });

  // Fetch all events for the club (they're already filtered to future events)
  const {
    data: allEvents = [],
    isLoading: eventsLoading,
    error: eventsError,
  } = useFetchClubEventsQuery({ clubId, includeAttendees: true });

  // Limit to events starting within the next 7 days so recurring events don't flood the feed.
  // If nothing is within 7 days, show the single next upcoming event so the feed isn't empty.
  const events = useMemo(() => {
    const oneWeekFromNow = new Date();
    oneWeekFromNow.setDate(oneWeekFromNow.getDate() + 7);
    const withinWeek = allEvents.filter((e) => {
      const start = new Date(e.occurrence_start);
      return start <= oneWeekFromNow;
    });
    if (withinWeek.length > 0) return withinWeek;
    // allEvents is sorted by occurrence_start ascending, so [0] is the soonest
    return allEvents.length > 0 ? [allEvents[0]] : [];
  }, [allEvents]);

  // Update accumulated posts when new data arrives
  useEffect(() => {
    if (postsData?.posts) {
      if (postsOffset === 0) {
        setAllPosts(postsData.posts);
      } else {
        setAllPosts((prev) => {
          const existingIds = new Set(prev.map((p) => p.id));
          const newPosts = postsData.posts.filter((p) => !existingIds.has(p.id));
          return [...prev, ...newPosts];
        });
      }
      setHasMorePosts(postsData.hasMore);
    }
  }, [postsData?.posts, postsOffset]);

  // Reset when clubId or userId changes
  useEffect(() => {
    setPostsOffset(0);
    setAllPosts([]);
    setHasMorePosts(true);
  }, [clubId, userId]);

  // Load more posts
  const loadMore = useCallback(() => {
    if (!postsFetching && hasMorePosts) {
      setPostsOffset((prev) => prev + POSTS_PAGE_SIZE);
    }
  }, [postsFetching, hasMorePosts]);

  // Intersection observer for infinite scroll
  const { ref: sentinelRef, isIntersecting } = useIntersectionObserver({
    enabled: hasMorePosts && !postsFetching && allPosts.length > 0,
    rootMargin: "200px",
  });

  // Trigger load more when sentinel is visible
  useEffect(() => {
    if (isIntersecting) {
      loadMore();
    }
  }, [isIntersecting, loadMore]);

  // Fetch club members with profiles for mention map
  const { data: members } = useFetchClubMembersQuery({
    clubId,
    includeUserProfiles: true,
  });

  // Build mention map from members
  const mentionMap = useMemo(() => {
    if (!members) return {};
    const membersForMention = members.map((m) => ({
      user_id: m.user_id,
      user_profiles: m.user_profile,
    }));
    return buildMentionMap(membersForMention);
  }, [members]);

  // Get current user's role in this club
  const userRole = useMemo(() => {
    if (!members || !userId) return undefined;
    return members.find((m) => m.user_id === userId)?.role;
  }, [members, userId]);

  // Build hosts map for event cards
  const hostsMap = useMemo(() => {
    if (!members || events.length === 0) return new Map<string, EventCardHost[]>();
    const map = new Map<string, EventCardHost[]>();

    for (const event of events) {
      const rootEvent = event.rootEvent;
      if (!rootEvent) continue;

      const hosts: EventCardHost[] = [];

      // Creator first
      const creatorMember = members.find((m) => m.user_id === rootEvent.creator_id);
      if (creatorMember?.user_profile) {
        const p = creatorMember.user_profile;
        hosts.push({
          userId: creatorMember.user_id,
          firstName: p.first_name || "Unknown",
          lastName: p.last_name || "",
          avatarUrl: p.avatar_small || p.avatar_medium || undefined,
        });
      }

      // Then owner/cohost members (not already added as creator)
      for (const m of members) {
        if (m.user_id === rootEvent.creator_id) continue;
        if (isOwnerOrCohost(m.role)) {
          const p = m.user_profile;
          if (p) {
            hosts.push({
              userId: m.user_id,
              firstName: p.first_name || "Unknown",
              lastName: p.last_name || "",
              avatarUrl: p.avatar_small || p.avatar_medium || undefined,
            });
          }
        }
      }

      map.set(event.id, hosts);
    }

    return map;
  }, [members, events]);

  // Posts to display
  const displayPosts = allPosts.length > 0 ? allPosts : (postsData?.posts ?? []);

  // Collect all unique author IDs from posts
  const authorIds = useMemo(() => {
    const ids = new Set<string>();
    displayPosts.forEach((post) => {
      if (post.author_id) {
        ids.add(post.author_id);
      }
    });
    return Array.from(ids);
  }, [displayPosts]);

  // Fetch author profiles
  const { data: authors } = useFetchUserProfilesByIdsQuery(authorIds, {
    skip: authorIds.length === 0,
  });

  // Create author lookup map
  const authorMap = useMemo(() => {
    if (!authors) return new Map();
    return new Map(authors.map((author) => [author.id, author]));
  }, [authors]);

  // Merge posts and events into a single sorted feed
  // Pinned posts go first (sorted by pinned_date), then everything else by created_at
  const feedItems: FeedItem[] = useMemo(() => {
    const items: FeedItem[] = [];

    // Add posts
    displayPosts.forEach((post) => {
      items.push({
        type: "post",
        data: post,
        createdAt: new Date(post.created_at || 0),
      });
    });

    // Add events (using occurrence_start so recurring events sort by when they happen)
    events.forEach((event) => {
      items.push({
        type: "event",
        data: event,
        createdAt: new Date(event.occurrence_start || 0),
      });
    });

    // Separate pinned posts from everything else
    const pinnedItems = items.filter(
      (item) => item.type === "post" && item.data.pinned
    );
    const nonPinnedItems = items.filter(
      (item) => item.type !== "post" || !item.data.pinned
    );

    // Sort pinned posts by pinned_date (newest first), fallback to created_at
    pinnedItems.sort((a, b) => {
      const postA = a.data as Post;
      const postB = b.data as Post;
      const dateA = new Date(postA.pinned_date || postA.created_at || 0);
      const dateB = new Date(postB.pinned_date || postB.created_at || 0);
      return dateB.getTime() - dateA.getTime();
    });

    // Sort non-pinned items by created_at descending (newest first)
    nonPinnedItems.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());

    // Return pinned posts first, then everything else
    return [...pinnedItems, ...nonPinnedItems];
  }, [displayPosts, events]);

  // Loading state
  const isLoading = postsLoading && eventsLoading && feedItems.length === 0;

  if (isLoading) {
    return <FeedSkeleton />;
  }

  // Error state
  if ((postsError || eventsError) && feedItems.length === 0) {
    return (
      <div className="p-6 text-center">
        <p className="text-text-secondary">Failed to load content.</p>
        <p className="text-text-tertiary text-sm mt-1">Please try again later.</p>
      </div>
    );
  }

  // Empty state - still show post prompt inside container if available
  if (!isLoading && feedItems.length === 0) {
    return (
      <div className="pt-2">
        <div className="bg-background border border-border-tertiary rounded-3xl  overflow-hidden">
          {/* Post prompt at top if available */}
          {postPrompt && (
            <>
              {postPrompt}
              <div className="border-t border-border-tertiary" />
            </>
          )}
          {/* Empty state content */}
          <div className="p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-surface-2 flex items-center justify-center">
              <Icon name="sparkles" size={32} className="text-text-tertiary" />
            </div>
            <p className="text-text-secondary font-medium">Nothing happening yet</p>
            <p className="text-text-tertiary text-sm mt-1">
              Be the first to share something with the club.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-2">
      {/* Feed container with border, rounded corners, and shadow per Figma */}
      <div className="bg-background border border-border-tertiary rounded-3xl  overflow-hidden pb-4">
        {/* Post prompt at top if available */}
        {postPrompt && (
          <>
            {postPrompt}
            <div className="border-t border-border-tertiary" />
          </>
        )}
        {feedItems.map((item, index) => {
          const isLast = index === feedItems.length - 1;

          if (item.type === "post") {
            const post = item.data;
            const author = authorMap.get(post.author_id ?? "");
            return (
              <div key={`post-${post.id}`}>
                <PostCard
                  post={post}
                  author={author}
                  clubId={clubId}
                  currentUserId={userId}
                  userRole={userRole}
                  onClick={onPostClick ? () => onPostClick(post.id) : undefined}
                  onCommentClick={onCommentClick ? () => onCommentClick(post.id) : undefined}
                  onEdit={onEditPost}
                  mentionMap={mentionMap}
                />
                {!isLast && <div className="border-t border-border-tertiary" />}
              </div>
            );
          } else {
            const event = item.data;
            return (
              <div key={`event-${event.id}`}>
                {onEventClick ? (
                  <div
                    onClick={() => onEventClick(event.id)}
                    className="block px-5 py-4 w-full text-left cursor-pointer"
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onEventClick(event.id);
                      }
                    }}
                  >
                    <EventCard
                      event={event}
                      currentUserId={userId}
                      hosts={hostsMap.get(event.id)}
                      showClubInfo={false}
                      canEdit={canEditEvent?.(event)}
                      canDelete={canDeleteEvent?.(event)}
                      onEdit={() => onEditEvent?.(event)}
                      onDelete={() => onDeleteEvent?.(event)}
                    />
                  </div>
                ) : (
                  <Link
                    to="/events/$identifier"
                    params={{ identifier: event.id }}
                    className="block px-5 py-4"
                  >
                    <EventCard
                      event={event}
                      currentUserId={userId}
                      hosts={hostsMap.get(event.id)}
                      showClubInfo={false}
                      canEdit={canEditEvent?.(event)}
                      canDelete={canDeleteEvent?.(event)}
                      onEdit={() => onEditEvent?.(event)}
                      onDelete={() => onDeleteEvent?.(event)}
                    />
                  </Link>
                )}
                {!isLast && <div className="border-t border-border-tertiary" />}
              </div>
            );
          }
        })}

        {/* Load more sentinel */}
        {hasMorePosts && (
          <div ref={sentinelRef} className="h-10 flex items-center justify-center">
            {postsFetching && (
              <div className="text-text-tertiary text-sm">Loading more...</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function FeedSkeleton() {
  return (
    <div className="p-4 space-y-4">
      {[1, 2, 3].map((i) => (
        <Skeleton key={i} className="h-32 w-full rounded-xl" />
      ))}
    </div>
  );
}
